import { styled } from "@mui/material/styles";
import { FormattedMessage, useIntl } from "react-intl";

import { FileFieldBase } from "@/common/components/forms/file-field/FileField";
import { LANG_KEYS } from "@/shared/lang/lang.constants";

import { CZECH_COUNTRY_CODE } from "./farmImport.schema";
import { ImportHelp } from "./ImportHelp";
import { SHAPEFILE_INVALID_MESSAGE_ID } from "./signup.api";

type ShapefileValidity = "invalid" | "unknown" | "valid";

type Props = {
  countryCode: string;
  validity: ShapefileValidity;
  value: File | null;
  error?: string;
  invalidMessageId?: string;
  onFileChange: (file: File | null) => void;
};

const ShapeFileUpload = ({
  countryCode,
  error,
  invalidMessageId = SHAPEFILE_INVALID_MESSAGE_ID,
  onFileChange,
  validity,
  value,
}: Props): React.ReactElement => {
  const { formatMessage, locale } = useIntl();

  const showHelp =
    locale === LANG_KEYS.CZ && countryCode === CZECH_COUNTRY_CODE;

  const help = showHelp ? (
    <ImportHelp
      copy={formatMessage({ id: "ShapefileImport.copy.part1" })}
      linkCopy={formatMessage({ id: "ShapefileImport.copy.part2" })}
    />
  ) : null;

  let validityMessage = null;
  if (validity === "valid") {
    validityMessage = (
      <ValidMessage data-testid="shape-file-valid">
        <FormattedMessage id="validation.fileIsValid" />
      </ValidMessage>
    );
  } else if (validity === "invalid") {
    validityMessage = (
      <InvalidMessage data-testid="shape-file-error">
        <FormattedMessage id={invalidMessageId} />
      </InvalidMessage>
    );
  }

  return (
    <Wrapper>
      <FileFieldBase
        error={error}
        fullWidth
        name="shapeFile"
        onChange={onFileChange}
        placeholder={formatMessage({ id: "ShapefileImport.lpisShapefileHint" })}
        value={value}
      />
      {help}
      {validityMessage}
      <Info data-testid="shape-file-info">
        <FormattedMessage id="ShapefileImport.info" />
      </Info>
    </Wrapper>
  );
};

const Wrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  width: "100%",
});

const Message = styled("p")({
  fontSize: 12,
  textAlign: "center",
  margin: 0,
});

const ValidMessage = styled(Message)(({ theme }) => ({
  color: theme.palette.cf.cfPrimary,
}));

const InvalidMessage = styled(Message)(({ theme }) => ({
  color: theme.palette.error.main,
}));

const Info = styled("p")(({ theme }) => ({
  fontSize: 12,
  textAlign: "center",
  margin: 0,
  color: theme.palette.cf.cfDarkerGrey,
}));

export { ShapeFileUpload, type ShapefileValidity };
